import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';

import { cn } from '@/shared/lib/cn';

import { buttonClasses } from './buttonStyles';
import type { ButtonShape, ButtonSize, ButtonVariant } from './buttonStyles';
import { Spinner } from './Spinner';

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  shape?: ButtonShape;
  /** Shows a spinner and blocks interaction. Width does not jump: label stays mounted. */
  loading?: boolean;
  fullWidth?: boolean;
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
  type?: 'button' | 'submit' | 'reset';
}

/**
 * The base button. WalletFront had no button primitive at all — every screen
 * restyled a bare `<button>` in its own SCSS module, which is why nothing there
 * had a consistent disabled or loading state.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    shape = 'rounded',
    loading = false,
    fullWidth = false,
    leadingIcon,
    trailingIcon,
    disabled,
    className,
    children,
    type = 'button',
    ...rest
  },
  ref
) {
  const isDisabled = disabled === true || loading;

  return (
    <button
      ref={ref}
      type={type}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      className={buttonClasses({
        variant,
        size,
        shape,
        fullWidth,
        ...(className === undefined ? {} : { className })
      })}
      {...rest}
    >
      {loading && (
        <span className="absolute inset-0 grid place-items-center">
          <Spinner size={size === 'lg' ? 'md' : 'sm'} />
        </span>
      )}
      {/*
        The label keeps its box while loading so the button does not resize and
        shove the layout around; only its visibility changes.
      */}
      <span className={cn('inline-flex items-center gap-2', loading && 'invisible')}>
        {leadingIcon}
        {children}
        {trailingIcon}
      </span>
    </button>
  );
});
